"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FunctionsNormalizedSchema = exports.FunctionScoreRowSchema = exports.ComponentFunctionDenormalizedSchema = exports.ComponentFunctionSignalDenormalizedSchema = exports.ALLOWED_RESOURCES = exports.ALLOWED_BUS_ROLES = exports.ALLOWED_FUNCTION_CLASSES = void 0;
exports.convertComponentFunctionDenormalizedToIComponentFunction = convertComponentFunctionDenormalizedToIComponentFunction;
exports.convertIComponentFunctionToComponentFunctionDenormalized = convertIComponentFunctionToComponentFunctionDenormalized;
exports.convertFunctionsToScoreRows = convertFunctionsToScoreRows;
exports.convertIComponentFunctionsToScoreRows = convertIComponentFunctionsToScoreRows;
exports.remapSignalIdsInFunctions = remapSignalIdsInFunctions;
const zod_1 = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
exports.ALLOWED_FUNCTION_CLASSES = [
    'bus.i2c.controller',
    'bus.i2c.peripheral',
    'bus.spi.controller',
    'bus.spi.peripheral',
];
exports.ALLOWED_BUS_ROLES = ['controller', 'peripheral'];
exports.ALLOWED_RESOURCES = ['i2c', 'spi'];
exports.ComponentFunctionSignalDenormalizedSchema = zod_1.z.object({
    name: zod_1.z.string().describe('The name of the function signal (e.g., "SCL", "SDA", "MOSI")'),
    signalNames: zod_1.z.array(zod_1.z.string()).describe('Array of component signal names that this function signal maps to'),
    busType: zod_1.z
        .nativeEnum(sigma_engine_shared_1.DesignModels.ComponentFunctionBusType)
        .nullable()
        .describe('The type of bus if this is a communication bus signal'),
    signalTypes: zod_1.z
        .array(zod_1.z.nativeEnum(sigma_engine_shared_1.DesignModels.SignalDirection))
        .nullable()
        .describe('Array of allowed signal directions'),
    optional: zod_1.z.boolean().describe('Whether this function signal is optional'),
});
exports.ComponentFunctionDenormalizedSchema = zod_1.z.object({
    name: zod_1.z
        .string()
        .describe('The name of the function instance as it appears in the datasheet (e.g., "SPI1", "SPI2", "I2C1", "I2C2")'),
    functionClass: zod_1.z.enum(exports.ALLOWED_FUNCTION_CLASSES).nullable().describe('The function class this function belongs to'),
    busRole: zod_1.z.enum(exports.ALLOWED_BUS_ROLES).nullable().describe('The role of the function on the bus if applicable'),
    mustBeAssigned: zod_1.z.boolean().describe('Whether this function must be assigned'),
    resourceConsumed: zod_1.z.enum(exports.ALLOWED_RESOURCES).nullable().describe('The resource this function consumes'),
    functionSignals: zod_1.z.array(exports.ComponentFunctionSignalDenormalizedSchema).describe('Array of function signals'),
});
function convertComponentFunctionDenormalizedToIComponentFunction(denormalized, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const functionSignals = denormalized.functionSignals
        .map((signal) => {
        const functionSignal = {
            name: signal.name,
            signals: signal.signalNames.map((signalName) => ({ signal_name: signalName })),
        };
        if (signal.optional) {
            functionSignal.optional = true;
        }
        if (signal.busType !== null) {
            functionSignal.busType = signal.busType;
        }
        if (signal.signalTypes !== null && signal.signalTypes.length > 0) {
            functionSignal.signalTypes = signal.signalTypes;
        }
        const parseResult = sigma_engine_shared_1.DesignModels.IComponentFunctionSignalSchema.safeParse(functionSignal);
        if (!parseResult.success) {
            logger.warn('ConvertComponentFunctionDenormalized_InvalidSignalDropped', {
                functionName: denormalized.name,
                signalName: signal.name,
                errors: parseResult.error.flatten().fieldErrors,
            });
            return null;
        }
        return parseResult.data;
    })
        .filter((signal) => signal !== null);
    const componentFunction = {
        name: denormalized.name,
        functionSignals,
        mustBeAssigned: denormalized.mustBeAssigned,
    };
    if (denormalized.functionClass !== null) {
        componentFunction.functionClass = denormalized.functionClass;
    }
    if (denormalized.busRole !== null) {
        componentFunction.busRole = denormalized.busRole;
    }
    if (denormalized.resourceConsumed !== null) {
        componentFunction.resourceConsumed = denormalized.resourceConsumed;
    }
    const parseResult = sigma_engine_shared_1.DesignModels.IComponentFunctionSchema.safeParse(componentFunction);
    if (!parseResult.success) {
        logger.warn('ConvertComponentFunctionDenormalized_InvalidFieldsDropped', {
            functionName: denormalized.name,
            errors: parseResult.error.flatten().fieldErrors,
        });
        return {
            name: denormalized.name,
            functionSignals,
            mustBeAssigned: denormalized.mustBeAssigned,
        };
    }
    return parseResult.data;
}
function lookupSignalName(signalId, signals) {
    if (!signals) {
        return String(signalId);
    }
    const matchingSignal = signals.find((s) => s.id === signalId);
    return matchingSignal?.name ?? String(signalId);
}
function convertIComponentFunctionToComponentFunctionDenormalized(componentFunction, signals) {
    const functionSignals = componentFunction.functionSignals.map((signal) => {
        const signalNames = [];
        for (const relativeSignal of signal.signals) {
            if (relativeSignal.signal_name) {
                if (Array.isArray(relativeSignal.signal_name)) {
                    signalNames.push(...relativeSignal.signal_name);
                }
                else {
                    signalNames.push(relativeSignal.signal_name);
                }
            }
            else if (relativeSignal.signal) {
                const signalIds = Array.isArray(relativeSignal.signal) ? relativeSignal.signal : [relativeSignal.signal];
                signalNames.push(...signalIds.map((id) => lookupSignalName(id, signals)));
            }
        }
        return {
            name: signal.name,
            signalNames,
            busType: signal.busType ?? null,
            signalTypes: signal.signalTypes ?? null,
            optional: signal.optional ?? false,
        };
    });
    return {
        name: componentFunction.name,
        functionClass: componentFunction.functionClass ?? null,
        busRole: componentFunction.busRole ?? null,
        mustBeAssigned: componentFunction.mustBeAssigned ?? false,
        resourceConsumed: componentFunction.resourceConsumed ?? null,
        functionSignals,
    };
}
exports.FunctionScoreRowSchema = zod_1.z.object({
    functionName: zod_1.z.string(),
    functionClass: zod_1.z.enum(exports.ALLOWED_FUNCTION_CLASSES).nullable(),
    busRole: zod_1.z.enum(exports.ALLOWED_BUS_ROLES).nullable(),
    resourceConsumed: zod_1.z.enum(exports.ALLOWED_RESOURCES).nullable(),
    functionSignalName: zod_1.z.string(),
    signalNames: zod_1.z.string(),
    optional: zod_1.z.boolean(),
});
exports.FunctionsNormalizedSchema = zod_1.z.object({
    functions: zod_1.z.array(exports.FunctionScoreRowSchema),
});
function convertFunctionsToScoreRows(functions) {
    return functions.flatMap((func) => func.functionSignals.map((signal) => ({
        functionName: func.name,
        functionClass: func.functionClass,
        busRole: func.busRole,
        resourceConsumed: func.resourceConsumed,
        functionSignalName: signal.name,
        signalNames: signal.signalNames.sort().join(','),
        optional: signal.optional,
    })));
}
function convertIComponentFunctionsToScoreRows(functions, signals) {
    const denormalized = functions.map((f) => convertIComponentFunctionToComponentFunctionDenormalized(f, signals));
    return convertFunctionsToScoreRows(denormalized);
}
const signal_id_remap_internal_1 = require("../signal-hygiene/signal-id-remap-internal");
function remapSignalIdsInFunctions(functions, signalIdMap, opts) {
    if (functions.length === 0) {
        return [];
    }
    const cloned = JSON.parse(JSON.stringify(functions));
    cloned.forEach((fn, i) => {
        const fss = fn.functionSignals ?? [];
        fss.forEach((fs, fi) => {
            const sigs = fs.signals ?? [];
            sigs.forEach((rs, si) => {
                (0, signal_id_remap_internal_1.remapRelativeSignal)(rs, signalIdMap, `functions[${i}].functionSignals[${fi}].signals[${si}]`, opts);
            });
        });
    });
    return cloned;
}
//# sourceMappingURL=component-function-denormalized.js.map